import {
  Box, ScrollArea, ActionIcon, Divider, type MantineStyleProp, Textarea, Group, Stack, useMantineTheme, Tooltip
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconMessage2X, IconSend2 } from '@tabler/icons-react';
import { useSelector } from 'react-redux';

import classes from './ResourceAssistant.module.css';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import AssistantChatMessage from './AssistantChatMessage';
import HumanChatMessage from './HumanChatMessage';
import { type ConversationMessage, type WithoutPermissions } from '../../types/apiTypes';
import { QueryResourcesSelector } from '../DropdownMenus';
import { selectResourceAssistantState } from '../../redux/store';
import CenteredLoader from '../CenteredLoader';

interface Props {
  style?: MantineStyleProp
}

const PROPOSAL_RESOURCES_PLACEHOLDER_TEXT = 'Ask about the resources selected for your proposal...';
const ALL_RESOURCES_PLACEHOLDER_TEXT = 'Ask about the resources available for this organization...';

const ResourceAssistantChat: React.FC<Props> = (props: Props) => {
  const { style } = props;
  const { organization, proposalUid } = useParams();
  const { colors, other: colorVars } = useMantineTheme();
  const [refetchingEnabled, setRefetchingEnabled] = useState(true);
  const [userMessage, setUserMessage] = useState('');
  const isProposalConversation = proposalUid !== undefined;
  const createConversationMutation = useEnsisMutation('/app/conversations', {
    requestType: 'post',
    showSuccessMessage: false,
    queryKeysToInvalidate: ['/app/conversations']
  });

  const handleCreateConversation = useCallback(() => {
    createConversationMutation.mutate({
      organization_slug: organization ?? '',
      proposal_uid: proposalUid
    });
  }, [organization, proposalUid]);

  const { data } = useEnsisQuery('/app/conversations', {
    queryParams: {
      organization_slug: organization ?? '',
      proposal_uid: proposalUid
    },
    refetchInterval: refetchingEnabled ? 4000 : undefined,
    onSuccess: (data) => {
      if (data === null && !createConversationMutation.isPending) {
        handleCreateConversation();
      }
    }
  });

  const deleteConversationMutation = useEnsisMutation('/app/conversations', {
    requestType: 'delete',
    showSuccessMessage: false,
    queryKeysToInvalidate: ['/app/conversations'],
    onSuccess: handleCreateConversation
  });

  const resourceAssistantState = useSelector(selectResourceAssistantState);
  const filterByProposal = isProposalConversation && resourceAssistantState.filterByProposal;

  const messages = data?.messages ?? [];
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const placeholderText = filterByProposal ? PROPOSAL_RESOURCES_PLACEHOLDER_TEXT : ALL_RESOURCES_PLACEHOLDER_TEXT;
  // Scroll to the bottom whenever a new message is sent
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (scrollArea !== null) {
      scrollArea.scrollTo({
        top: scrollArea.scrollHeight
      });
    }
  }, [messages]);

  const numberOfMessages = messages.length;

  useEffect(() => {
    setRefetchingEnabled(isThinking);
  }, [data]);
  const conversationUid = data?.uid ?? '';

  const sendMessageMutation = useEnsisMutation(`/app/conversations/${conversationUid}/messages`, {
    queryKeysToInvalidate: ['/app/conversations'],
    showSuccessMessage: false
  });

  const retryMessageMutation = useEnsisMutation(`/app/conversations/${conversationUid}/messages`, {
    requestType: 'patch',
    queryKeysToInvalidate: ['/app/conversations'],
    showSuccessMessage: false
  });

  const handleRetryMessage = useCallback(
    (aiMessageUid: string, userMessageUid: string) => {
      retryMessageMutation.mutate({
        user_message_uid: userMessageUid,
        ai_message_uid: aiMessageUid,
        filter_resources_by_proposal: filterByProposal
      });
    }, [retryMessageMutation]
  );

  const isThinking = (messages.length > 0 && messages[numberOfMessages - 1].status === 'thinking') ||
    sendMessageMutation.isPending ||
    retryMessageMutation.isPending;
  const loading = (
    createConversationMutation.isPending ||
    deleteConversationMutation.isPending
  );
  const canSendMessage = userMessage.trim() !== '' && !loading && !isThinking;

  const handleSendMessage = useCallback(() => {
    if (canSendMessage) {
      sendMessageMutation.mutate({
        content: userMessage,
        filter_resources_by_proposal: filterByProposal
      }, {
        onSuccess: () => {
          setUserMessage('');
        }
      });
    }
  }, [userMessage, sendMessageMutation, conversationUid]);

  const handleClearMessages = useCallback(() => {
    deleteConversationMutation.mutate({
      organization_slug: organization ?? '',
      proposal_uid: proposalUid
    });
  }, [
    deleteConversationMutation.mutate,
    organization,
    proposalUid
  ]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const disabled = !canSendMessage;
  const disabledIconColor = colors.gray[5];
  const sendIconColor = disabled ? disabledIconColor : 'white';

  const clearChatDisabled = loading || isThinking || messages.length <= 1;
  const clearChatIconColor = clearChatDisabled ? disabledIconColor : colorVars.primaryIconColor;

  const sendButton = (
    <ActionIcon
      size='md'
      disabled={disabled}
      onClick={handleSendMessage}
    >
      <IconSend2 size={20} color={sendIconColor} />
    </ActionIcon>
  );

  const clearChatButton = (
    <Tooltip
      label='Clear chat'
      events={{ hover: true, focus: false, touch: false }}
      position='top-end'
    >
      <ActionIcon
        size='md'
        style={{ border: `1px solid ${colors.gray[3]}` }}
        disabled={clearChatDisabled}
        variant='outline'
        onClick={handleClearMessages}
      >
        <IconMessage2X size={20} color={clearChatIconColor} />
      </ActionIcon>
    </Tooltip>
  );

  const todayDivider = <Divider label='Today' p={8} />;

  const firstMessageOfTodayIndex = messages.findIndex(
    (message: WithoutPermissions<ConversationMessage>) => {
      const todaysDate = new Date().toLocaleDateString();
      const messageDate = new Date(message?.created_at ?? '').toLocaleDateString();
      return messageDate === todaysDate;
    }
  );

  const getMessageComponent = (
    message: WithoutPermissions<ConversationMessage>,
    index: number
  ) => {
    const showTodayDivider = index === firstMessageOfTodayIndex;
    const showRetryButton = index === messages.length - 1 && message.status === 'failed';
    let messageComponent = (
      <Group justify='flex-end'>
        <HumanChatMessage message={message} />
      </Group>
    );
    if (message.sender === 'ai') {
      messageComponent = (
        <AssistantChatMessage
          message={message}
          handleRetryMessage={showRetryButton
            ? () => { handleRetryMessage(message.uid ?? '', messages[index - 1].uid ?? ''); }
            : undefined}
        />
      );
    }
    return (
      <Stack gap={0} mb={8} key={message.uid}>
        {showTodayDivider && todayDivider}
        {messageComponent}
      </Stack>
    );
  };

  return (
    <Stack
      gap={0}
      style={style}
      align='stretch'
    >
      <ScrollArea
        flex={1}
        viewportRef={scrollAreaRef}
        classNames={{
          root: classes.messagesScrollAreaRoot,
          content: classes.messagesScrollAreaContent
        }}
      >
        {
          loading
            ? <CenteredLoader h={300} />
            : (
                <Stack gap={0} align='stretch'>
                  {messages.map((message: WithoutPermissions<ConversationMessage>, index) => {
                    return getMessageComponent(message, index);
                  })}
                </Stack>
              )
        }
      </ScrollArea>
      <Box className={classes.bottomOuterContainer}>
        <Stack gap={4} className={classes.textInputContainer}>
          <Textarea
            variant='unstyled'
            placeholder={placeholderText}
            value={userMessage}
            onKeyDown={onKeyDown}
            onChange={(e) => { setUserMessage(e.currentTarget.value); }}
            autosize
            maxRows={2}
            minRows={1}
          />
          <Group justify='space-between'>
            <Group gap={4}>
              {clearChatButton}
              {
                isProposalConversation && (
                  <QueryResourcesSelector disabled={loading} />
                )
              }
            </Group>
            {sendButton}
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ResourceAssistantChat;
